"use client"
import { createContext, useContext, useState, useEffect, useCallback } from "react"
import { getLevelConfig, getTotalLevels, getSessionConfig, getSessionBoard, SESSION_TIMER_MODE } from "../storyMode/config/levelConfigs"

export const StoryModeContext = createContext()

export function StoryModeProvider({ children }) {
  // Progress state only - game state is in GameContext
  const [currentLevelIndex, setCurrentLevelIndex] = useState(1)
  const [unlockedLevels, setUnlockedLevels] = useState([1])
  const [completedLevels, setCompletedLevels] = useState([])
  const [seenTutorials, setSeenTutorials] = useState({})

  // Session mode state
  const [activeSession, setActiveSession] = useState(null)       // 'beginner' | 'intermediate' | 'advanced' | null
  const [currentBoardIndex, setCurrentBoardIndex] = useState(0)  // 0-based index within session
  const [sessionTimer, setSessionTimer] = useState(null)         // remaining seconds for continuous timer
  const [sessionStats, setSessionStats] = useState([])           // per-board stats accumulator
  const [completedSessions, setCompletedSessions] = useState([]) // which tiers have been finished

  // Load saved progress on mount
  useEffect(() => {
    loadStoryProgress()
  }, [])

  // Save progress when state changes
  useEffect(() => {
    saveStoryProgress()
  }, [currentLevelIndex, unlockedLevels, completedLevels, seenTutorials])

  // Load story mode progress from localStorage
  const loadStoryProgress = useCallback(() => {
    try {
      const saved = localStorage.getItem('storyModeProgress')
      if (saved) {
        const progress = JSON.parse(saved)
        setCurrentLevelIndex(progress.currentLevelIndex || 1)
        setUnlockedLevels(progress.unlockedLevels || [1])
        setCompletedLevels(progress.completedLevels || [])
        setSeenTutorials(progress.seenTutorials || {})
      }
    } catch (error) {
      console.warn('Failed to load story mode progress:', error)
    }
  }, [])

  // Save story mode progress to localStorage
  const saveStoryProgress = useCallback(() => {
    try {
      const progress = {
        currentLevelIndex,
        unlockedLevels,
        completedLevels,
        seenTutorials,
        lastSaved: new Date().toISOString()
      }
      localStorage.setItem('storyModeProgress', JSON.stringify(progress))
    } catch (error) {
      console.warn('Failed to save story mode progress:', error)
    }
  }, [currentLevelIndex, unlockedLevels, completedLevels, seenTutorials])

  // Get current level configuration
  const getCurrentLevelConfig = useCallback(() => {
    return getLevelConfig(currentLevelIndex)
  }, [currentLevelIndex])

  // Check if a level is unlocked
  const isLevelUnlocked = useCallback((levelIndex) => {
    return unlockedLevels.includes(levelIndex)
  }, [unlockedLevels])

  // Check if a level is completed
  const isLevelCompleted = useCallback((levelIndex) => {
    return completedLevels.includes(levelIndex)
  }, [completedLevels])

  // Unlock a level (called when starting a level)
  const unlockLevel = useCallback((levelIndex) => {
    if (!unlockedLevels.includes(levelIndex)) {
      setUnlockedLevels(prev => [...prev, levelIndex])
    }
  }, [unlockedLevels])

  // Complete current level
  const completeLevel = useCallback(() => {
    // Add to completed levels
    if (!completedLevels.includes(currentLevelIndex)) {
      setCompletedLevels(prev => [...prev, currentLevelIndex])
    }

    // Unlock next level
    const nextLevelIndex = currentLevelIndex + 1
    if (nextLevelIndex <= getTotalLevels() && !unlockedLevels.includes(nextLevelIndex)) {
      setUnlockedLevels(prev => [...prev, nextLevelIndex])
    }
  }, [currentLevelIndex, completedLevels, unlockedLevels])

  // Mark tutorial as seen
  const markTutorialSeen = useCallback((tutorialId) => {
    setSeenTutorials(prev => ({
      ...prev,
      [tutorialId]: true
    }))
  }, [])

  // Reset story mode progress
  const resetStoryProgress = useCallback(() => {
    setCurrentLevelIndex(1)
    setUnlockedLevels([1])
    setCompletedLevels([])
    setSeenTutorials({})
  }, [])

  // ── Session mode ──────────────────────────────────────────

  const startSession = useCallback((tierId) => {
    const session = getSessionConfig(tierId)
    if (!session) return
    setActiveSession(tierId)
    setCurrentBoardIndex(0)
    setSessionStats([])
    if (session.timerMode === SESSION_TIMER_MODE.CONTINUOUS) {
      setSessionTimer(session.totalTimeLimitSeconds)
    } else {
      setSessionTimer(null)
    }
  }, [])

  const getCurrentSessionConfig = useCallback(() => {
    return activeSession ? getSessionConfig(activeSession) : null
  }, [activeSession])

  const getCurrentBoardConfig = useCallback(() => {
    return activeSession ? getSessionBoard(activeSession, currentBoardIndex) : null
  }, [activeSession, currentBoardIndex])

  // Called when a board is cleared. Returns 'next' | 'complete'.
  const completeBoard = useCallback((boardStats) => {
    const session = getSessionConfig(activeSession)
    if (!session) return 'complete'

    const updatedStats = [...sessionStats, { boardIndex: currentBoardIndex, ...boardStats }]
    setSessionStats(updatedStats)

    const nextIndex = currentBoardIndex + 1
    const hasNextBoard = nextIndex < session.boards.length

    if (hasNextBoard) {
      setCurrentBoardIndex(nextIndex)
      return 'next'
    } else {
      if (!completedSessions.includes(activeSession)) {
        setCompletedSessions(prev => [...prev, activeSession])
      }
      return 'complete'
    }
  }, [activeSession, currentBoardIndex, sessionStats, completedSessions])

  const exitSession = useCallback(() => {
    setActiveSession(null)
    setCurrentBoardIndex(0)
    setSessionTimer(null)
    setSessionStats([])
  }, [])

  const isSessionCompleted = useCallback((tierId) => {
    return completedSessions.includes(tierId)
  }, [completedSessions])

  // Get level progress statistics
  const getProgressStats = useCallback(() => {
    return {
      totalLevels: getTotalLevels(),
      completedLevels: completedLevels.length,
      unlockedLevels: unlockedLevels.length,
      currentLevel: currentLevelIndex,
      completionPercentage: Math.round((completedLevels.length / getTotalLevels()) * 100),
      nextLevel: getCurrentLevelConfig() ? currentLevelIndex + 1 : null
    }
  }, [completedLevels, unlockedLevels, currentLevelIndex, getCurrentLevelConfig])

  // Check if player can access a level
  const canAccessLevel = useCallback((levelIndex) => {
    return levelIndex >= 1 && levelIndex <= getTotalLevels() && isLevelUnlocked(levelIndex)
  }, [isLevelUnlocked])

  // Get all levels with their status
  const getAllLevelsStatus = useCallback(() => {
    const levels = []
    for (let i = 1; i <= getTotalLevels(); i++) {
      const config = getLevelConfig(i)
      levels.push({
        ...config,
        unlocked: isLevelUnlocked(i),
        completed: isLevelCompleted(i),
        isCurrent: i === currentLevelIndex
      })
    }
    return levels
  }, [isLevelUnlocked, isLevelCompleted, currentLevelIndex])

  const contextValue = {
    // State
    currentLevelIndex,
    unlockedLevels,
    completedLevels,
    seenTutorials,

    // Methods
    setCurrentLevelIndex,
    unlockLevel,
    completeLevel,
    markTutorialSeen,
    resetStoryProgress,
    getProgressStats,
    canAccessLevel,
    getAllLevelsStatus,
    getCurrentLevelConfig,
    isLevelUnlocked,
    isLevelCompleted,

    // Session state
    activeSession,
    currentBoardIndex,
    sessionTimer,
    setSessionTimer,
    sessionStats,
    completedSessions,

    // Session methods
    startSession,
    getCurrentSessionConfig,
    getCurrentBoardConfig,
    completeBoard,
    exitSession,
    isSessionCompleted
  }

  return (
    <StoryModeContext.Provider value={contextValue}>
      {children}
    </StoryModeContext.Provider>
  )
}

// Hook to use Story Mode context
export function useStoryMode() {
  const context = useContext(StoryModeContext)
  if (!context) {
    throw new Error('useStoryMode must be used within a StoryModeProvider')
  }
  return context
}
